<template>
  <div class="ads-management-page">
    <div class="ads-mgmt-header">
      <div>
        <h2 class="ads-mgmt-title">Ads Management</h2>
        <p class="ads-mgmt-subtitle">View, edit, add, and moderate all ads from one place.</p>
      </div>
      <div class="ads-mgmt-header-actions">
        <b-button variant="outline-secondary" class="mr-1" @click="goToPendingAds">
          Pending
        </b-button>
        <b-button variant="outline-secondary" class="mr-1" @click="goToUploadedAds">
          Uploaded
        </b-button>
        <b-button variant="primary" class="add-ad-btn" @click="openCreateAdModal">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          Add New Ad
        </b-button>
      </div>
    </div>

    <b-card no-body class="ads-mgmt-card">
      <div class="ads-mgmt-toolbar">
        <div class="ads-mgmt-toolbar__left">
          <v-select
            v-model="perPage"
            :clearable="false"
            :searchable="false"
            class="entries-select"
            :options="['08', '10', '20', '50']"
            @input="reloadPageOne"
          />
          <div class="view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              class="view-toggle__btn"
              :class="{ 'is-active': viewMode === 'list' }"
              title="List view"
              @click="viewMode = 'list'"
            >
              <feather-icon icon="ListIcon" size="16" />
            </button>
            <button
              type="button"
              class="view-toggle__btn"
              :class="{ 'is-active': viewMode === 'grid' }"
              title="Grid view"
              @click="viewMode = 'grid'"
            >
              <feather-icon icon="GridIcon" size="16" />
            </button>
          </div>
        </div>

        <div class="ads-mgmt-toolbar__right">
          <b-form-input
            v-model="search"
            placeholder="Search title, company..."
            class="search-input"
            @input="debouncedSearch"
          />
          <v-select
            v-model="statusFilter"
            :clearable="false"
            :searchable="false"
            class="filter-select"
            :reduce="option => option.value"
            label="label"
            :options="statusOptions"
            @input="reloadPageOne"
          />
          <v-select
            v-model="categoryFilter"
            :clearable="true"
            class="filter-select"
            :reduce="option => option.value"
            label="label"
            :options="categoryFilterOptions"
            placeholder="Category"
            @input="reloadPageOne"
          />
          <v-select
            v-model="advertiserFilter"
            :clearable="true"
            class="filter-select filter-select--wide"
            :reduce="option => option.value"
            label="label"
            :options="advertiserFilterOptions"
            placeholder="Advertiser"
            @input="reloadPageOne"
          />
          <v-select
            v-model="sortFilter"
            :clearable="false"
            :searchable="false"
            class="filter-select"
            :reduce="option => option.value"
            label="label"
            :options="sortOptions"
            @input="reloadPageOne"
          />
        </div>
      </div>

      <b-alert v-if="dashboardError" show variant="danger" class="mx-1 mb-1">
        {{ dashboardError }}
        <b-button size="sm" variant="outline-danger" class="ml-1" @click="loadAdsDashboard">Retry</b-button>
      </b-alert>

      <div v-if="isDashboardLoading" class="text-center py-3">
        <b-spinner label="Loading ads..." />
      </div>
      <div v-else-if="!rows.length" class="empty-state">
        <feather-icon icon="MonitorIcon" size="42" class="empty-state__icon" />
        <h4>No ads found</h4>
        <p>Try adjusting filters or create a new ad.</p>
        <b-button variant="primary" @click="openCreateAdModal">Add New Ad</b-button>
      </div>

      <template v-else>
        <!-- LIST VIEW (default) -->
        <b-table
          v-if="viewMode === 'list'"
          :items="rows"
          :fields="tableFields"
          responsive
          class="mb-0 ads-datatable"
          tbody-tr-class="ad-row"
        >
          <template #cell(no)="data">
            <span class="id-cell">{{ data.item.no }}</span>
          </template>

          <template #cell(ad)="data">
            <div class="ad-cell cursor-pointer" @click="openAdDetail(data.item.id)">
              <b-img :src="data.item.image" class="ad-thumb" alt="" />
              <div class="ad-cell__meta">
                <span class="ad-name">{{ data.item.title }}</span>
                <small class="ad-cat">{{ data.item.category || '—' }}</small>
              </div>
            </div>
          </template>

          <template #cell(company)="data">
            <div class="company-cell">
              <b-avatar size="32" :src="data.item.companyLogo" class="mr-50" />
              <span>{{ data.item.companyName }}</span>
            </div>
          </template>

          <template #cell(status)="data">
            <span class="status-pill" :class="statusClass(data.item.status)">
              {{ data.item.statusLabel }}
            </span>
          </template>

          <template #cell(stats)="data">
            <div class="stats-cell">
              <span>{{ data.item.views }} views</span>
              <span>{{ data.item.likes }} likes</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <row-actions
              show-view
              @view="openAdDetail(data.item.id)"
              @edit="openEditAdModal(data.item)"
              @delete="confirmDeleteAd(data.item.id)"
            >
              <template #extra>
                <button
                  v-if="data.item.canApprove"
                  type="button"
                  class="row-action row-action--success"
                  title="Approve"
                  @click="openApproveModal(data.item.id)"
                >
                  <feather-icon icon="CheckCircleIcon" size="16" />
                </button>
                <button
                  v-if="data.item.canReject"
                  type="button"
                  class="row-action row-action--warning"
                  title="Reject"
                  @click="openRejectForAd(data.item.id)"
                >
                  <feather-icon icon="XCircleIcon" size="16" />
                </button>
              </template>
            </row-actions>
          </template>
        </b-table>

        <!-- GRID VIEW -->
        <div v-else class="ads-grid">
          <b-card
            v-for="ad in rows"
            :key="`grid-${ad.id}`"
            no-body
            class="ads-grid-card"
          >
            <div class="ads-grid-card__media cursor-pointer" @click="openAdDetail(ad.id)">
              <b-img :src="ad.image" fluid class="ads-grid-card__img" />
              <span class="status-pill status-pill--overlay" :class="statusClass(ad.status)">
                {{ ad.statusLabel }}
              </span>
            </div>
            <b-card-body class="ads-grid-card__body">
              <div class="d-flex align-items-start justify-content-between mb-50">
                <h5 class="ads-grid-card__title mb-0 cursor-pointer" @click="openAdDetail(ad.id)">
                  {{ ad.title }}
                </h5>
              </div>
              <div class="company-cell mb-50">
                <b-avatar size="28" :src="ad.companyLogo" class="mr-50" />
                <small>{{ ad.companyName }}</small>
              </div>
              <small class="text-muted d-block mb-1">
                {{ ad.category || '—' }} · {{ ad.views }} views · {{ ad.likes }} likes
              </small>
              <row-actions
                show-view
                @view="openAdDetail(ad.id)"
                @edit="openEditAdModal(ad)"
                @delete="confirmDeleteAd(ad.id)"
              >
                <template #extra>
                  <button
                    v-if="ad.canApprove"
                    type="button"
                    class="row-action row-action--success"
                    title="Approve"
                    @click="openApproveModal(ad.id)"
                  >
                    <feather-icon icon="CheckCircleIcon" size="16" />
                  </button>
                  <button
                    v-if="ad.canReject"
                    type="button"
                    class="row-action row-action--warning"
                    title="Reject"
                    @click="openRejectForAd(ad.id)"
                  >
                    <feather-icon icon="XCircleIcon" size="16" />
                  </button>
                </template>
              </row-actions>
            </b-card-body>
          </b-card>
        </div>
      </template>

      <AdminPaginationBar
        :page="currentPage"
        :per-page="perPageNum"
        :total="totalRows"
        @change="onPageChange"
      />
    </b-card>

    <b-modal
      v-model="uploadModalOpen"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      modal-class="create-ad-modal-wrap"
      content-class="create-ad-modal-content"
      @shown="onUploadModalShown"
      @hidden="onUploadModalHidden"
    >
      <div class="create-ad-modal">
        <button
          type="button"
          class="create-ad-modal-close"
          aria-label="Close"
          @click="cancelUploadModal"
        >
          <img src="/svg/Close.svg" alt="" width="32" height="32">
        </button>

        <div class="create-ad-layout">
          <aside class="create-ad-sidebar">
            <div class="create-ad-sidebar__intro">
              <h2 class="create-ad-sidebar__title">
                {{ isEditMode ? 'Edit Ad' : 'Create New Ad' }}
                <feather-icon icon="ZapIcon" size="18" class="create-ad-title-icon" />
              </h2>
              <p class="create-ad-sidebar__subtitle">
                {{ isEditMode ? 'Update ad details and save your changes.' : 'Fill in the details to create and publish your ad.' }}
              </p>
            </div>

            <div class="create-ad-illustration" aria-hidden="true">
              <div class="create-ad-illustration__card">
                <span class="create-ad-illustration__badge">AD</span>
                <span class="create-ad-illustration__line"></span>
                <span class="create-ad-illustration__line create-ad-illustration__line--short"></span>
              </div>
              <span class="create-ad-illustration__plus">+</span>
            </div>

            <nav class="create-ad-steps" aria-label="Create ad steps">
              <button
                v-for="step in uploadSteps"
                :key="step.id"
                type="button"
                class="create-ad-step"
                :class="{ 'is-active': activeUploadStep === step.id }"
                @click="goToUploadStep(step.id)"
              >
                <span class="create-ad-step__num">{{ step.num }}</span>
                <span class="create-ad-step__text">
                  <span class="create-ad-step__label">{{ step.label }}</span>
                  <span class="create-ad-step__hint">{{ step.hint }}</span>
                </span>
              </button>
            </nav>
          </aside>

          <div class="create-ad-main">
            <validation-observer ref="uploadAdFormObserver">
              <section v-if="activeUploadStep === 'basic'" class="create-ad-section">
                <header class="create-ad-section__head">
                  <span class="create-ad-section__icon">
                    <feather-icon icon="FileTextIcon" size="18" />
                  </span>
                  <div>
                    <h3 class="create-ad-section__title">1. Basic Information</h3>
                  </div>
                </header>

                <div class="create-ad-fields">
                  <mypoda-form-field
                    label="Title"
                    name="Ad title"
                    rules="required|max:255"
                    required-mark
                  >
                    <template #default="{ state, inputId }">
                      <b-form-input
                        :id="inputId"
                        v-model="uploadAdForm.title"
                        :state="state"
                        class="create-ad-input"
                        placeholder="Enter ad title"
                      />
                    </template>
                  </mypoda-form-field>

                  <mypoda-form-field
                    label="Description"
                    name="Description"
                    rules="required|max:5000"
                    required-mark
                  >
                    <template #default="{ state, inputId }">
                      <b-form-input
                        :id="inputId"
                        v-model="uploadAdForm.description"
                        :state="state"
                        class="create-ad-input"
                        placeholder="Enter ad description"
                      />
                    </template>
                  </mypoda-form-field>

                  <b-row>
                    <b-col md="6">
                      <mypoda-form-field
                        label="Primary Category"
                        name="Primary Category"
                        rules="required"
                        required-mark
                      >
                        <template #default="{ state, inputId }">
                          <b-form-select
                            :id="inputId"
                            v-model="uploadAdForm.categoryId"
                            :options="uploadCategoryOptions"
                            :state="state"
                            class="create-ad-input"
                            @change="onPrimaryCategoryChange"
                          >
                            <template #first>
                              <b-form-select-option :value="null" disabled>
                                Select a category
                              </b-form-select-option>
                            </template>
                          </b-form-select>
                        </template>
                      </mypoda-form-field>
                    </b-col>
                    <b-col md="6">
                      <mypoda-form-field
                        label="Secondary Category"
                        name="Secondary Category"
                      >
                        <template #default="{ state, inputId }">
                          <b-form-select
                            :id="inputId"
                            v-model="uploadAdForm.secondaryCategoryId"
                            :options="uploadSecondaryCategoryOptions"
                            :state="state"
                            class="create-ad-input"
                            :disabled="!uploadAdForm.categoryId"
                          >
                            <template #first>
                              <b-form-select-option :value="null">
                                {{ uploadSecondaryCategoryPlaceholder }}
                              </b-form-select-option>
                            </template>
                          </b-form-select>
                        </template>
                      </mypoda-form-field>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col md="6">
                      <mypoda-form-field
                        label="Select Ad Type"
                        name="Select Ad Type"
                        rules="required"
                        required-mark
                      >
                        <template #default="{ state, inputId }">
                          <b-form-select
                            :id="inputId"
                            v-model="uploadAdForm.adType"
                            :options="uploadAdTypeOptions"
                            :state="state"
                            class="create-ad-input"
                          />
                        </template>
                      </mypoda-form-field>
                    </b-col>
                    <b-col md="6">
                      <mypoda-form-field
                        label="Age Restriction"
                        name="Age Restriction"
                      >
                        <template #default="{ state, inputId }">
                          <b-form-select
                            :id="inputId"
                            v-model="uploadAdForm.minAge"
                            :options="uploadAgeRestrictionOptions"
                            :state="state"
                            class="create-ad-input"
                          />
                        </template>
                      </mypoda-form-field>
                    </b-col>
                  </b-row>

                  <mypoda-form-field
                    label="Select Advertiser"
                    name="Advertiser"
                    rules="required"
                    required-mark
                  >
                    <template #default="{ state, inputId }">
                      <b-form-select
                        :id="inputId"
                        v-model="uploadAdForm.company"
                        :options="uploadCompanyOptions"
                        :state="state"
                        class="create-ad-input"
                        @change="onUploadAdvertiserChange"
                      />
                    </template>
                  </mypoda-form-field>

                  <mypoda-form-field
                    label="Select Brand"
                    name="Brand"
                    rules="required"
                    required-mark
                  >
                    <template #default="{ state, inputId }">
                      <b-form-select
                        :id="inputId"
                        v-model="uploadAdForm.brandId"
                        :options="uploadBrandOptions"
                        :state="state"
                        :disabled="!uploadAdForm.company"
                        class="create-ad-input"
                      />
                    </template>
                  </mypoda-form-field>
                  <p v-if="uploadAdForm.company && !uploadBrands.length" class="create-ad-field-error">
                    This advertiser has no brands.
                    <router-link :to="{ name: 'brands-management' }">Add a brand</router-link>
                    before creating the ad.
                  </p>

                  <p v-if="uploadCategoryError" class="create-ad-field-error">
                    {{ uploadCategoryError }}
                  </p>
                </div>
              </section>

              <section v-else-if="activeUploadStep === 'media'" class="create-ad-section">
                <header class="create-ad-section__head">
                  <span class="create-ad-section__icon">
                    <feather-icon icon="ImageIcon" size="18" />
                  </span>
                  <div>
                    <h3 class="create-ad-section__title">2. Media Upload</h3>
                  </div>
                </header>

                <input
                  ref="uploadModalFileInput"
                  type="file"
                  class="d-none"
                  :accept="uploadMediaAccept"
                  @change="onModalFilePick"
                >

                <div
                  class="create-ad-dropzone"
                  :class="{ 'is-dragging': isUploadDragActive, 'has-file': !!(selectedUploadFile || existingAdImage) }"
                  role="button"
                  tabindex="0"
                  @click="$refs.uploadModalFileInput.click()"
                  @keydown.enter.prevent="$refs.uploadModalFileInput.click()"
                  @dragenter.prevent="onUploadDragEnter"
                  @dragover.prevent="onUploadDragOver"
                  @dragleave.prevent="onUploadDragLeave"
                  @drop.prevent="onUploadDrop"
                >
                  <template v-if="uploadFilePreview">
                    <video
                      v-if="uploadPreviewIsVideo"
                      :src="uploadFilePreview"
                      class="create-ad-dropzone__preview"
                      muted
                      playsinline
                      controls
                    />
                    <b-img v-else :src="uploadFilePreview" fluid class="create-ad-dropzone__preview" />
                    <p class="create-ad-dropzone__filename">{{ selectedUploadFileName }}</p>
                    <button type="button" class="create-ad-dropzone__change" @click.stop="$refs.uploadModalFileInput.click()">
                      Change file
                    </button>
                  </template>
                  <template v-else>
                    <feather-icon icon="UploadCloudIcon" size="42" class="create-ad-dropzone__cloud" />
                    <p class="create-ad-dropzone__text">
                      Drag &amp; drop files here or <span>browse</span>
                    </p>
                    <p class="create-ad-dropzone__hint">
                      Recommended: 1080 x 1080 px | Max size: 20MB per file. {{ uploadMediaHint }}
                    </p>
                  </template>
                </div>
                <p v-if="uploadMediaError" class="create-ad-field-error">
                  {{ uploadMediaError }}
                </p>
              </section>

              <section v-else-if="activeUploadStep === 'cta'" class="create-ad-section">
                <header class="create-ad-section__head">
                  <span class="create-ad-section__icon">
                    <feather-icon icon="MousePointerIcon" size="18" />
                  </span>
                  <div>
                    <h3 class="create-ad-section__title">3. Call to Action</h3>
                  </div>
                </header>

                <b-row>
                  <b-col md="6">
                    <mypoda-form-field
                      label="CTA Button Text"
                      name="CTA label"
                      rules="required|max:255"
                      required-mark
                    >
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="TypeIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.ctaLabel"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                            placeholder="Enter CTA button text"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                  <b-col md="6">
                    <mypoda-form-field
                      label="Destination URL"
                      name="CTA link"
                      rules="required|url"
                      required-mark
                    >
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="LinkIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.ctaLink"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                            placeholder="Enter destination URL"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                </b-row>
              </section>

              <section v-else-if="activeUploadStep === 'schedule'" class="create-ad-section create-ad-section--last">
                <header class="create-ad-section__head">
                  <span class="create-ad-section__icon">
                    <feather-icon icon="CalendarIcon" size="18" />
                  </span>
                  <div>
                    <h3 class="create-ad-section__title">4. Publish &amp; Expiry Schedule</h3>
                  </div>
                </header>

                <div class="create-ad-schedule-summary">
                  <feather-icon icon="CalendarIcon" size="16" />
                  <span>{{ scheduleSummary }}</span>
                </div>

                <b-row>
                  <b-col md="6">
                    <mypoda-form-field label="Publish Date" name="Publish Date" rules="required">
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="CalendarIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.startDate"
                            type="date"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                  <b-col md="6">
                    <mypoda-form-field label="Publish Time" name="Publish Time" rules="required">
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="ClockIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.startTime"
                            type="time"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                  <b-col md="6">
                    <mypoda-form-field label="Expiry Date" name="Expiry Date" rules="required">
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="CalendarIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.endDate"
                            type="date"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                  <b-col md="6">
                    <mypoda-form-field label="Expiry Time" name="Expiry Time" rules="required">
                      <template #default="{ state, inputId }">
                        <div class="create-ad-input-icon-wrap">
                          <feather-icon icon="ClockIcon" size="16" class="create-ad-input-icon" />
                          <b-form-input
                            :id="inputId"
                            v-model="uploadAdForm.endTime"
                            type="time"
                            :state="state"
                            class="create-ad-input create-ad-input--with-icon"
                          />
                        </div>
                      </template>
                    </mypoda-form-field>
                  </b-col>
                </b-row>
                <p v-if="scheduleError" class="create-ad-field-error mt-50">{{ scheduleError }}</p>
              </section>
            </validation-observer>

            <footer class="create-ad-step-footer">
              <b-button
                v-if="activeUploadStepIndex > 0"
                type="button"
                variant="outline-secondary"
                class="create-ad-back-btn"
                :disabled="isUploadSubmitting"
                @click="goUploadPrevStep"
              >
                <feather-icon icon="ArrowLeftIcon" size="16" />
              </b-button>
              <div v-else class="create-ad-step-footer__spacer"></div>

              <div class="create-ad-step-footer__actions">
                <b-button
                  v-if="activeUploadStep === 'schedule'"
                  type="button"
                  variant="outline-secondary"
                  class="create-ad-cancel-btn"
                  :disabled="isUploadSubmitting"
                  @click="cancelUploadModal"
                >
                  Cancel
                </b-button>

                <b-button
                  v-if="activeUploadStep !== 'schedule'"
                  type="button"
                  class="create-ad-next-btn"
                  @click="goUploadNextStep"
                >
                  Next
                  <feather-icon icon="ArrowRightIcon" size="16" />
                </b-button>

                <b-button
                  v-else
                  type="button"
                  class="create-ad-submit-btn"
                  :disabled="isUploadSubmitting"
                  @click="validateAndOpenUploadSuccess"
                >
                  <b-spinner v-if="isUploadSubmitting" small class="mr-50" />
                  {{ isEditMode ? 'Save Changes' : 'Submit for Approval' }}
                  <feather-icon v-if="!isUploadSubmitting" icon="SendIcon" size="16" />
                </b-button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="uploadSuccessModalOpen"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      modal-class="upload-success-modal-wrap"
      content-class="upload-success-modal-content"
    >
      <div class="upload-success-modal-body">
        <h2 class="upload-success-modal-title">Upload Successful</h2>

        <b-img :src="iphoneThumb" class="upload-success-modal-img" />

        <div class="upload-success-grid">
          <input type="text" class="upload-success-field" :value="uploadAdForm.title" readonly>
          <input type="text" class="upload-success-field" :value="uploadAdForm.ctaLabel" readonly>
        </div>

        <input type="text" class="upload-success-field mb-1" :value="uploadCategoryLabel" readonly>

        <textarea
          class="upload-success-field upload-success-field--textarea mb-1"
          readonly
          :value="uploadAdForm.description"
        />

        <div class="upload-success-field upload-success-datetime mb-1">
          <span>{{ uploadScheduleLabel }}</span>
          <feather-icon icon="CalendarIcon" size="20" />
        </div>

        <div class="upload-success-footer">
          <b-button variant="outline-secondary" class="upload-success-secondary-btn mr-1" @click="dismissUploadSuccess">
            Close
          </b-button>
          <b-button variant="outline-primary" class="upload-success-secondary-btn mr-1" @click="editUploadedAd">
            Edit
          </b-button>
          <b-button class="upload-success-post-btn" @click="closeUploadSuccessModal">Post</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="approveModalOpen"
      centered
      hide-header
      hide-footer
      modal-class="approve-ad-modal-wrap"
      content-class="approve-ad-modal-content"
    >
      <div class="approve-ad-modal-body">
        <h2 class="approve-ad-modal-title">Ad Review</h2>

        <b-img :src="iphoneThumb" class="approve-ad-modal-img" />

        <div class="approve-ad-form-grid">
          <input type="text" class="approve-ad-field" :value="(featuredAd && featuredAd.title) || ''" readonly>
          <input type="text" class="approve-ad-field" :value="featuredAdCtaLabel" readonly>
        </div>

        <input type="text" class="approve-ad-field mb-1" :value="featuredAdCategory" readonly>

        <textarea
          class="approve-ad-field approve-ad-field--textarea mb-1"
          readonly
          :value="(featuredAd && featuredAd.description) || ''"
        />

        <div class="approve-ad-field approve-ad-datetime mb-1">
          <span>{{ featuredAdPostedTime }}</span>
          <feather-icon icon="CalendarIcon" size="20" />
        </div>

        <div class="approve-ad-modal-actions">
          <b-button class="approve-ad-modal-btn" @click="submitApprove">Approve</b-button>
          <b-button class="approve-ad-modal-btn" @click="closeApproveModal">Cancel</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="rejectModalOpen"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      modal-class="reject-ad-modal-wrap"
      content-class="reject-ad-modal-content"
    >
      <div class="reject-ad-modal-body">
        <h2 class="reject-ad-modal-title">Please write a reason for rejecting this ad.</h2>
        <b-form-textarea
          v-model="rejectReason"
          placeholder="Write a reason"
          rows="6"
          no-resize
          class="reject-reason-input"
        />
        <div class="reject-ad-modal-actions">
          <b-button class="reject-ad-modal-btn" @click="submitRejectReason">Submit</b-button>
          <b-button class="reject-ad-modal-btn" @click="closeRejectModal">Cancel</b-button>
        </div>
      </div>
    </b-modal>

    <admin-outcome-modal
      :visible="outcomeVisible"
      :variant="outcomeVariant"
      @close="outcomeVisible = false"
      @primary="onOutcomePrimary"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BImg,
  BModal,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import AdminOutcomeModal from '@/components/admin/AdminOutcomeModal.vue'
import RowActions from '@/components/admin/RowActions.vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import MypodaFormField from '@/components/mypoda/MypodaFormField.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import {
  applyListPagination,
  debounce,
  extractApiErrorMessage,
  formatAdminDate,
  isAdPendingStatus,
  resolveMediaUrl,
  unwrapAdminResponse,
} from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'
import {
  adMediaAccept,
  adMediaFormatError,
  adMediaFormatHint,
  fileMatchesAdType,
  isVideoMediaUrl,
} from '@/utils/adMedia'

export default {
  name: 'AdsManagement',
  components: {
    AdminOutcomeModal,
    RowActions,
    AdminPaginationBar,
    MypodaFormField,
    ValidationObserver,
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BImg,
    BModal,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      iphoneThumb: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=600&h=400&fit=crop',
      selectedAdId: null,
      isActionLoading: false,
      outcomeVisible: false,
      outcomeVariant: 'ad-approved',
      uploadModalOpen: false,
      uploadSuccessModalOpen: false,
      isUploadSubmitting: false,
      isUploadDragActive: false,
      selectedUploadFile: null,
      editingAdId: null,
      existingAdImage: '',
      uploadCompanies: [],
      uploadBrands: [],
      uploadAdForm: {
        title: '',
        description: '',
        ctaLabel: '',
        ctaLink: '',
        company: '',
        brandId: '',
        categoryId: null,
        secondaryCategoryId: null,
        minAge: 0,
        adType: 'image',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
      },
      uploadCategories: [],
      uploadCategoryError: '',
      uploadMediaError: '',
      activeUploadStep: 'basic',
      uploadFilePreviewUrl: '',
      uploadSteps: [
        { id: 'basic', num: 1, label: 'Basic Information', hint: 'Provide ad details' },
        { id: 'media', num: 2, label: 'Media Upload', hint: 'Add images or videos' },
        { id: 'cta', num: 3, label: 'Call to Action', hint: 'Set action and destination' },
        { id: 'schedule', num: 4, label: 'Publish & Expiry', hint: 'Set publish & expiry date/time' },
      ],
      scheduleError: '',
      uploadAdTypeOptions: [
        { value: 'image', text: 'Image' },
        { value: 'video', text: 'Video' },
      ],
      uploadAgeRestrictionOptions: [
        { value: 0, text: 'No age restriction' },
        { value: 13, text: '13 years and older' },
        { value: 16, text: '16 years and older' },
        { value: 18, text: '18 years and older' },
        { value: 21, text: '21 years and older' },
      ],
      approveModalOpen: false,
      rejectModalOpen: false,
      rejectReason: '',
      isDashboardLoading: false,
      dashboardError: '',
      featuredAd: null,
      rows: [],
      viewMode: 'list',
      search: '',
      statusFilter: 'all',
      categoryFilter: null,
      advertiserFilter: null,
      sortFilter: '-id',
      currentPage: 1,
      perPage: '10',
      totalRows: 0,
      statusOptions: [
        { value: 'all', label: 'All statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'draft', label: 'Draft' },
        { value: 'approved', label: 'Approved' },
        { value: 'active', label: 'Active' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' },
        { value: 'inactive', label: 'Inactive' },
      ],
      sortOptions: [
        { value: '-id', label: 'Newest first' },
        { value: 'id', label: 'Oldest first' },
        { value: 'title', label: 'Title A–Z' },
        { value: '-title', label: 'Title Z–A' },
        { value: '-views', label: 'Most views' },
        { value: '-likes', label: 'Most likes' },
        { value: 'status', label: 'Status' },
        { value: 'company', label: 'Company A–Z' },
      ],
      tableFields: [
        { key: 'no', label: 'No', thClass: 'text-nowrap' },
        { key: 'ad', label: 'Ad' },
        { key: 'company', label: 'Company' },
        { key: 'status', label: 'Status' },
        { key: 'stats', label: 'Stats' },
        { key: 'actions', label: 'Actions', thClass: 'text-right', tdClass: 'text-right' },
      ],
      debouncedSearch: null,
    }
  },
  computed: {
    isEditMode() {
      return Boolean(this.editingAdId)
    },
    perPageNum() {
      return Number(this.perPage) || 10
    },
    categoryFilterOptions() {
      return this.uploadCategories
        .filter(category => !category.parentId)
        .map(category => ({ value: category.id, label: category.name }))
    },
    advertiserFilterOptions() {
      return this.uploadCompanies.map(company => ({
        value: company.id,
        label: company.name,
      }))
    },
    featuredAdCtaLabel() {
      if (!this.featuredAd) return ''
      return this.featuredAd.cta
        || this.featuredAd.cta_label
        || this.featuredAd.cta_text
        || this.featuredAd.button_text
        || this.featuredAd.call_to_action
        || ''
    },
    featuredAdPostedTime() {
      if (!this.featuredAd) return ''
      if (this.featuredAd.start_date) return this.featuredAd.start_date
      if (this.featuredAd.publish_at) return this.featuredAd.publish_at
      if (this.featuredAd.company && this.featuredAd.company.posted_time) {
        return this.featuredAd.company.posted_time
      }

      return ''
    },
    featuredAdCategory() {
      if (!this.featuredAd) return ''
      return this.featuredAd.category
        || this.featuredAd.category_name
        || this.featuredAd.ad_category
        || this.featuredAd.type
        || ''
    },
    uploadCategoryLabel() {
      const match = this.uploadCategories.find(c => c.id === this.uploadAdForm.categoryId)
      return (match && match.name) || ''
    },
    uploadCategoryOptions() {
      return this.uploadCategories
        .filter(category => !category.parentId)
        .map(category => ({
          value: category.id,
          text: category.name,
        }))
    },
    uploadSecondaryCategoryPlaceholder() {
      if (!this.uploadAdForm.categoryId) return 'Select primary category first'
      if (!this.uploadSecondaryCategoryOptions.length) return 'No child categories for this primary'
      return 'None (optional)'
    },
    uploadSecondaryCategoryOptions() {
      const primaryId = Number(this.uploadAdForm.categoryId || 0)
      if (!primaryId) return []
      return this.uploadCategories
        .filter(category => Number(category.parentId) === primaryId)
        .map(category => ({
          value: category.id,
          text: category.name,
        }))
    },
    uploadFilePreview() {
      return this.uploadFilePreviewUrl || this.existingAdImage || ''
    },
    uploadPreviewIsVideo() {
      if (this.selectedUploadFile) return fileMatchesAdType(this.selectedUploadFile, 'video')
      return isVideoMediaUrl(this.existingAdImage)
    },
    uploadMediaAccept() {
      return adMediaAccept(this.uploadAdForm.adType)
    },
    uploadMediaHint() {
      return adMediaFormatHint(this.uploadAdForm.adType)
    },
    hasMatchingUploadMedia() {
      if (this.selectedUploadFile) return true
      if (!this.existingAdImage) return false
      const existingIsVideo = isVideoMediaUrl(this.existingAdImage)
      return this.uploadAdForm.adType === 'video' ? existingIsVideo : !existingIsVideo
    },
    selectedUploadFileName() {
      if (this.selectedUploadFile?.name) return this.selectedUploadFile.name
      if (this.existingAdImage) return this.uploadPreviewIsVideo ? 'Current video' : 'Current image'
      return ''
    },
    scheduleSummary() {
      const start = this.formatSchedulePart(this.uploadAdForm.startTime, this.uploadAdForm.startDate)
      const end = this.formatSchedulePart(this.uploadAdForm.endTime, this.uploadAdForm.endDate)
      if (!start && !end) return 'Select publish and expiry date & time'
      return `Publish ${start || '—'} → Expires ${end || '—'}`
    },
    uploadScheduleLabel() {
      return this.scheduleSummary
    },
    uploadBrandOptions() {
      if (!this.uploadAdForm.company) {
        return [{ value: '', text: 'Select an advertiser first', disabled: true }]
      }
      if (!this.uploadBrands.length) {
        return [{ value: '', text: 'No brands for this advertiser', disabled: true }]
      }
      return [
        { value: '', text: 'Select brand', disabled: true },
        ...this.uploadBrands.map(brand => ({
          value: String(brand.id),
          text: brand.name,
        })),
      ]
    },
    uploadCompanyOptions() {
      if (!this.uploadCompanies.length) {
        return [{ value: '', text: 'No advertisers available', disabled: true }]
      }
      return [
        { value: '', text: 'Select advertiser company', disabled: true },
        ...this.uploadCompanies.map(company => ({
          value: String(company.id),
          text: company.name,
        })),
      ]
    },
    uploadStepOrder() {
      return this.uploadSteps.map(step => step.id)
    },
    activeUploadStepIndex() {
      return this.uploadStepOrder.indexOf(this.activeUploadStep)
    },
  },
  watch: {
    'uploadAdForm.adType'(next, prev) {
      if (!prev || next === prev) return
      if (this.selectedUploadFile && !fileMatchesAdType(this.selectedUploadFile, next)) {
        this.revokeUploadFilePreview()
        this.selectedUploadFile = null
        this.uploadMediaError = adMediaFormatError(next)
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.reloadPageOne()
    }, 400)
    this.loadAdsDashboard()
    this.loadUploadCategories()
    this.loadUploadCompanies().then(() => {
      const editId = this.$route.query.edit
      if (editId) {
        this.openEditAdModal({ id: editId })
        const query = { ...this.$route.query }
        delete query.edit
        this.$router.replace({ query }).catch(() => {})
      }
    })
  },
  methods: {
    resolveMediaUrl,
    statusClass(status) {
      const s = String(status || '').toLowerCase()
      if (['approved', 'active', 'live'].includes(s)) return 'is-success'
      if (['rejected', 'expired', 'inactive'].includes(s)) return 'is-danger'
      if (['pending', 'draft'].includes(s)) return 'is-warning'
      return 'is-muted'
    },
    mapRow(ad) {
      const status = String(ad.status || ad.raw_status || '').toLowerCase() || 'draft'
      const pending = isAdPendingStatus(status) || status === 'draft'
      const mediaUrl =
        (Array.isArray(ad.media) && ad.media[0] && (ad.media[0].url || ad.media[0].thumbnail)) ||
        (Array.isArray(ad.gallery) && ad.gallery[0] && (ad.gallery[0].url || ad.gallery[0].thumbnail)) ||
        ad.thumbnail ||
        ''
      const imageRaw = ad.image || mediaUrl || ''
      return {
        id: ad.id,
        no: String(ad.no ?? ad.id).padStart(2, '0'),
        title: ad.title || 'Untitled ad',
        description: ad.description || '',
        image: resolveMediaUrl(imageRaw) || '',
        category: ad.category || ad.category_name || '',
        categoryId: ad.category_id || null,
        secondaryCategoryId: ad.secondary_category_id || null,
        companyName: ad.company?.name || '—',
        companyLogo: resolveMediaUrl(ad.company?.logo || ad.brand?.logo),
        companyId: ad.company?.id || ad.advertiser_id || null,
        status,
        statusLabel: status ? status.charAt(0).toUpperCase() + status.slice(1) : '—',
        views: Number(ad.views || 0),
        likes: Number(ad.likes || 0),
        canApprove: pending || Boolean(ad.actions?.approve),
        canReject: pending || Boolean(ad.actions?.reject),
        cta: ad.cta || ad.cta_label || ad.cta_text || '',
        ctaLink: ad.cta_link || '',
        minAge: Number(ad.min_age || 0),
        publishAt: ad.publish_at || '',
        expiresAt: ad.expires_at || '',
        createdAt: formatAdminDate(ad.created_at || ''),
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadAdsDashboard()
    },
    reloadPageOne() {
      this.currentPage = 1
      this.loadAdsDashboard()
    },
    async loadUploadCategories() {
      try {
        const res = await adminPanelApi.categories()
        const payload = unwrapAdminResponse(res)
        const list = payload?.categories || []
        this.uploadCategories = (Array.isArray(list) ? list : [])
          .filter(item => item.is_active !== false)
          .map(item => {
            const parentId = item.parent_id != null ? Number(item.parent_id) : null
            return {
              id: item.id,
              name: item.name,
              parentId: Number.isFinite(parentId) && parentId > 0 ? parentId : null,
              icon: (item.icon || item.name || '?').slice(0, 2).toUpperCase(),
            }
          })
        if (this.uploadCategories.length && !this.uploadAdForm.categoryId && !this.isEditMode) {
          const firstPrimary = this.uploadCategoryOptions[0]
          this.uploadAdForm.categoryId = firstPrimary ? firstPrimary.value : null
        }
      } catch (e) {
        this.uploadCategories = []
      }
    },
    onPrimaryCategoryChange() {
      this.uploadCategoryError = ''
      this.uploadAdForm.secondaryCategoryId = null
    },
    async loadAdsDashboard() {
      this.isDashboardLoading = true
      this.dashboardError = ''
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPageNum,
          sort: this.sortFilter || '-id',
        }
        const q = (this.search || '').trim()
        if (q) params.search = q
        if (this.statusFilter && this.statusFilter !== 'all') params.status = this.statusFilter
        if (this.categoryFilter) params.category_id = this.categoryFilter
        if (this.advertiserFilter) params.advertiser_id = this.advertiserFilter

        const res = await adminPanelApi.ads(params)
        const payload = unwrapAdminResponse(res)
        applyListPagination(this, res, payload)
        const ads = Array.isArray(payload?.ads) ? payload.ads : []
        this.rows = ads.map(ad => this.mapRow(ad))
      } catch (e) {
        this.dashboardError = e.response?.data?.message || 'Unable to load ads.'
        this.rows = []
      } finally {
        this.isDashboardLoading = false
      }
    },
    async loadUploadCompanies() {
      try {
        const res = await adminPanelApi.advertisers({ page: 1, per_page: 100 })
        const payload = unwrapAdminResponse(res)
        const list = payload.advertisers || []
        this.uploadCompanies = list.map(item => ({
          id: item.id || item.company_id,
          name: item.company_name || item.companyName || `Company #${item.id || item.company_id}`,
        }))
        if (!this.uploadAdForm.company && this.uploadCompanies.length && !this.isEditMode) {
          this.uploadAdForm.company = String(this.uploadCompanies[0].id)
        }
        await this.loadUploadBrands(this.uploadAdForm.company)
      } catch (e) {
        this.uploadCompanies = []
      }
    },
    async onUploadAdvertiserChange(companyId) {
      const fromEvent = companyId != null && typeof companyId !== 'object' ? companyId : null
      this.uploadAdForm.brandId = ''
      await this.$nextTick()
      await this.loadUploadBrands(fromEvent ?? this.uploadAdForm.company)
    },
    async loadUploadBrands(companyId) {
      const id = Number(companyId || this.uploadAdForm.company || 0)
      if (!id) {
        this.uploadBrands = []
        return
      }
      try {
        const res = await adminPanelApi.brands({ company_id: id })
        const payload = unwrapAdminResponse(res)
        const list = Array.isArray(payload?.brands) ? payload.brands : []
        this.uploadBrands = list.filter(brand => brand && brand.id && brand.is_active !== false)
        const selectedStillValid = this.uploadBrands.some(brand => String(brand.id) === String(this.uploadAdForm.brandId))
        if (!selectedStillValid) {
          this.uploadAdForm.brandId = this.uploadBrands.length === 1 ? String(this.uploadBrands[0].id) : ''
        }
      } catch (e) {
        this.uploadBrands = []
      }
    },
    openCreateAdModal() {
      this.editingAdId = null
      this.existingAdImage = ''
      this.resetUploadForm()
      this.uploadModalOpen = true
      this.loadUploadBrands(this.uploadAdForm.company)
    },
    async openEditAdModal(item) {
      if (!item?.id) return
      this.editingAdId = item.id
      this.resetUploadForm()
      this.isUploadSubmitting = true
      try {
        const res = await adminPanelApi.adPreview(item.id)
        const ad = unwrapAdminResponse(res)
        this.populateFormFromAd(ad)
        this.uploadModalOpen = true
      } catch (e) {
        this.editingAdId = null
        notifyError(this, e.response?.data?.message || 'Unable to load ad for editing.')
      } finally {
        this.isUploadSubmitting = false
      }
    },
    parseScheduleParts(rawDateTime, fallbackDate = '', fallbackTimeDisplay = '') {
      const value = String(rawDateTime || '').trim()
      // Y-m-d H:i[:s] or ISO without timezone shift
      const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?/)
      if (iso) {
        return { date: `${iso[1]}-${iso[2]}-${iso[3]}`, time: `${iso[4]}:${iso[5]}` }
      }
      // m-d-Y from preview display fields
      const mdy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/)
      let date = ''
      if (mdy) {
        date = `${mdy[3]}-${mdy[1]}-${mdy[2]}`
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        date = value
      } else if (fallbackDate) {
        const fb = this.parseScheduleParts(fallbackDate)
        date = fb.date
      }

      let time = ''
      const timeStr = String(fallbackTimeDisplay || '').trim()
      const ampm = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (ampm) {
        let hours = Number(ampm[1])
        const minutes = ampm[2]
        const period = ampm[3].toUpperCase()
        if (period === 'PM' && hours < 12) hours += 12
        if (period === 'AM' && hours === 12) hours = 0
        time = `${String(hours).padStart(2, '0')}:${minutes}`
      } else if (/^\d{2}:\d{2}/.test(timeStr)) {
        time = timeStr.slice(0, 5)
      }

      return { date, time }
    },
    populateFormFromAd(ad) {
      const schedule = this.defaultScheduleDates()
      const start = this.parseScheduleParts(
        ad.publish_at_raw || '',
        ad.publish_at || ad.start_date || '',
        ad.publish_time || ad.scheduled_at?.time || '',
      )
      const end = this.parseScheduleParts(
        ad.expires_at_raw || '',
        ad.expires_at || ad.end_date || '',
        ad.expires_time || '',
      )
      this.uploadAdForm = {
        title: ad.title || '',
        description: ad.description || '',
        ctaLabel: ad.cta_label || ad.cta || '',
        ctaLink: ad.cta_link || '',
        company: String(ad.advertiser_id || ad.company?.id || ''),
        brandId: ad.brand_id ? String(ad.brand_id) : (ad.brand?.id ? String(ad.brand.id) : ''),
        categoryId: ad.category_id || null,
        secondaryCategoryId: ad.secondary_category_id || null,
        minAge: Number(ad.min_age || 0),
        adType: isVideoMediaUrl(ad.image || ad.media?.[0]?.url) ? 'video' : 'image',
        startDate: start.date || schedule.startDate,
        endDate: end.date || schedule.endDate,
        startTime: start.time || schedule.startTime,
        endTime: end.time || schedule.endTime,
      }
      this.scheduleError = ''
      this.loadUploadBrands(this.uploadAdForm.company)
      this.existingAdImage = resolveMediaUrl(ad.image) || ''
      this.iphoneThumb = this.existingAdImage || this.iphoneThumb
      this.selectedUploadFile = null
      this.revokeUploadFilePreview()
      this.activeUploadStep = 'basic'
    },
    validateScheduleFields() {
      this.scheduleError = ''
      const { startDate, endDate, startTime, endTime } = this.uploadAdForm
      if (!startDate || !endDate || !startTime || !endTime) {
        this.scheduleError = 'Publish and expiry date/time are required.'
        return false
      }
      const start = new Date(`${startDate}T${startTime}:00`)
      const end = new Date(`${endDate}T${endTime}:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        this.scheduleError = 'Invalid publish or expiry date/time.'
        return false
      }
      if (end <= start) {
        this.scheduleError = 'Expiry date/time must be after publish date/time.'
        return false
      }
      return true
    },
    resetUploadForm() {
      const schedule = this.defaultScheduleDates()
      this.uploadAdForm = {
        title: '',
        description: '',
        ctaLabel: '',
        ctaLink: '',
        company: this.uploadCompanies[0] ? String(this.uploadCompanies[0].id) : '',
        brandId: '',
        categoryId: this.uploadCategoryOptions[0]?.value ?? null,
        secondaryCategoryId: null,
        minAge: 0,
        adType: 'image',
        startDate: schedule.startDate,
        endDate: schedule.endDate,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
      }
      this.uploadCategoryError = ''
      this.uploadMediaError = ''
      this.scheduleError = ''
      this.activeUploadStep = 'basic'
      this.revokeUploadFilePreview()
      this.selectedUploadFile = null
      this.existingAdImage = ''
      if (!this.isEditMode) {
        this.editingAdId = null
      }
      if (this.$refs.uploadAdFormObserver) {
        this.$refs.uploadAdFormObserver.reset()
      }
    },
    defaultScheduleDates() {
      // Default to "now" (local) so newly created ads are not stuck waiting until a
      // hardcoded 10:15 wall-clock — that caused ~10–12h delays after approval.
      const pad = n => String(n).padStart(2, '0')
      const start = new Date()
      const end = new Date(start)
      end.setDate(end.getDate() + 7)
      const toLocalDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      const toLocalTime = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`
      return {
        startDate: toLocalDate(start),
        endDate: toLocalDate(end),
        startTime: toLocalTime(start),
        endTime: toLocalTime(end),
      }
    },
    formatSchedulePart(time, date) {
      if (!date) return ''
      const formattedDate = this.formatScheduleDate(date)
      const formattedTime = this.formatScheduleTime(time)
      if (formattedTime && formattedDate) return `${formattedTime} | ${formattedDate}`
      return formattedDate || formattedTime
    },
    formatScheduleDate(value) {
      if (!value) return ''
      const date = new Date(`${value}T00:00:00`)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    formatScheduleTime(value) {
      if (!value) return ''
      const [hours, minutes] = String(value).split(':').map(Number)
      if (Number.isNaN(hours)) return value
      const period = hours >= 12 ? 'PM' : 'AM'
      const hour12 = hours % 12 || 12
      return `${hour12}:${String(minutes || 0).padStart(2, '0')} ${period}`
    },
    revokeUploadFilePreview() {
      if (this.uploadFilePreviewUrl) {
        URL.revokeObjectURL(this.uploadFilePreviewUrl)
        this.uploadFilePreviewUrl = ''
      }
    },
    setSelectedUploadFile(file) {
      if (!file || !fileMatchesAdType(file, this.uploadAdForm.adType)) {
        const message = adMediaFormatError(this.uploadAdForm.adType)
        this.uploadMediaError = message
        notifyError(this, message)
        return false
      }
      this.revokeUploadFilePreview()
      this.selectedUploadFile = file
      this.uploadFilePreviewUrl = URL.createObjectURL(file)
      this.uploadMediaError = ''
      return true
    },
    onUploadModalShown() {
      this.activeUploadStep = this.selectedUploadFile ? 'media' : 'basic'
    },
    onUploadModalHidden() {
      this.revokeUploadFilePreview()
    },
    goToUploadStep(stepId) {
      const targetIdx = this.uploadStepOrder.indexOf(stepId)
      if (targetIdx === -1 || targetIdx > this.activeUploadStepIndex) return
      this.activeUploadStep = stepId
    },
    goUploadPrevStep() {
      const prev = this.uploadStepOrder[this.activeUploadStepIndex - 1]
      if (prev) {
        this.activeUploadStep = prev
      }
    },
    validateBasicFields() {
      if (!this.uploadAdForm.title.trim() || !this.uploadAdForm.description.trim()) {
        return false
      }
      if (!this.uploadAdForm.categoryId) {
        this.uploadCategoryError = 'Please select a category.'
        return false
      }
      if (!this.uploadAdForm.company) {
        return false
      }
      this.uploadCategoryError = ''
      return true
    },
    validateCtaFields() {
      return Boolean(
        this.uploadAdForm.ctaLabel.trim()
        && this.uploadAdForm.ctaLink.trim(),
      )
    },
    async goUploadNextStep() {
      if (this.activeUploadStep === 'basic') {
        const valid = await this.$refs.uploadAdFormObserver.validate()
        if (!valid || !this.validateBasicFields()) return
        this.activeUploadStep = 'media'
        return
      }
      if (this.activeUploadStep === 'media') {
        if (!this.hasMatchingUploadMedia) {
          this.uploadMediaError = this.uploadAdForm.adType === 'video'
            ? 'Please upload a video for your ad.'
            : 'Please upload an image for your ad.'
          return
        }
        this.uploadMediaError = ''
        this.activeUploadStep = 'cta'
        return
      }
      if (this.activeUploadStep === 'cta') {
        const valid = await this.$refs.uploadAdFormObserver.validate()
        if (!valid || !this.validateCtaFields()) return
        this.activeUploadStep = 'schedule'
      }
    },
    onModalFilePick(event) {
      const [file] = event.target.files || []
      if (event.target) {
        event.target.value = ''
      }
      if (file) {
        this.setSelectedUploadFile(file)
      }
    },
    cancelUploadModal() {
      this.uploadModalOpen = false
      this.editingAdId = null
      this.existingAdImage = ''
      this.resetUploadForm()
    },
    onUploadDragEnter() {
      this.isUploadDragActive = true
    },
    onUploadDragOver() {
      this.isUploadDragActive = true
    },
    onUploadDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isUploadDragActive = false
      }
    },
    onUploadDrop(event) {
      this.isUploadDragActive = false
      const [file] = event.dataTransfer?.files || []
      if (file) {
        this.setSelectedUploadFile(file)
      }
    },
    closeUploadModal() {
      this.uploadModalOpen = false
    },
    async validateAndOpenUploadSuccess() {
      if (!this.validateBasicFields()) {
        this.activeUploadStep = 'basic'
        return
      }
      if (!this.hasMatchingUploadMedia) {
        this.uploadMediaError = this.uploadAdForm.adType === 'video'
          ? 'Please upload a video for your ad.'
          : 'Please upload an image for your ad.'
        this.activeUploadStep = 'media'
        return
      }
      this.uploadMediaError = ''
      if (!this.validateCtaFields()) {
        this.activeUploadStep = 'cta'
        return
      }
      if (!this.validateScheduleFields()) {
        this.activeUploadStep = 'schedule'
        return
      }
      const valid = await this.$refs.uploadAdFormObserver.validate()
      if (!valid) return
      if (this.isEditMode) {
        await this.submitUploadAd()
        return
      }
      this.openUploadSuccessModal()
    },
    async submitUploadAd() {
      if (!this.hasMatchingUploadMedia) {
        notifyError(this, this.uploadAdForm.adType === 'video'
          ? 'Please upload a video for your ad.'
          : 'Please upload an image for your ad.')
        return
      }
      if (!this.uploadAdForm.company) {
        notifyError(this, 'Please select an advertiser company.')
        return
      }
      if (!this.uploadAdForm.brandId) {
        notifyError(this, 'Please select a brand.')
        this.activeUploadStep = 'basic'
        return
      }
      if (!this.uploadAdForm.categoryId) {
        notifyError(this, 'Please select a primary category.')
        this.activeUploadStep = 'basic'
        return
      }
      if (this.uploadAdForm.secondaryCategoryId) {
        const secondaryId = Number(this.uploadAdForm.secondaryCategoryId)
        const primaryId = Number(this.uploadAdForm.categoryId)
        const allowed = this.uploadSecondaryCategoryOptions.some(opt => Number(opt.value) === secondaryId)
        if (!allowed || secondaryId === primaryId) {
          notifyError(this, 'Secondary category must belong to the selected primary category.')
          this.activeUploadStep = 'basic'
          return
        }
      }
      if (!this.validateScheduleFields()) {
        this.activeUploadStep = 'schedule'
        return
      }

      this.isUploadSubmitting = true
      let imageUrl = ''
      if (this.selectedUploadFile) {
        try {
          const uploadForm = new FormData()
          uploadForm.append('file', this.selectedUploadFile)
          const uploadRes = await adminPanelApi.uploadMedia(uploadForm)
          const uploadPayload = unwrapAdminResponse(uploadRes)
          imageUrl = uploadPayload?.url || ''
        } catch (e) {
          this.isUploadSubmitting = false
          notifyError(this, e.response?.data?.message || 'Unable to upload media.')
          return
        }
      }

      const form = new FormData()
      if (imageUrl) {
        form.append('image_url', imageUrl)
      } else if (this.selectedUploadFile) {
        form.append('image', this.selectedUploadFile)
      }
      form.append('title', this.uploadAdForm.title || 'New ad')
      form.append('description', this.uploadAdForm.description || '')
      form.append('cta_label', this.uploadAdForm.ctaLabel || 'Learn more')
      form.append('cta_link', this.uploadAdForm.ctaLink || 'https://example.com')
      form.append('advertiser_id', String(this.uploadAdForm.company))
      form.append('brand_id', String(this.uploadAdForm.brandId || ''))
      form.append('category_id', String(this.uploadAdForm.categoryId || ''))
      if (this.uploadAdForm.secondaryCategoryId) {
        form.append('secondary_category_id', String(this.uploadAdForm.secondaryCategoryId))
      } else if (this.isEditMode) {
        form.append('secondary_category_id', '')
      }
      form.append('min_age', String(this.uploadAdForm.minAge ?? 0))
      form.append('ad_type', this.uploadAdForm.adType || 'image')
      if (this.uploadAdForm.startDate) {
        form.append('start_date', this.uploadAdForm.startDate)
      }
      if (this.uploadAdForm.endDate) {
        form.append('end_date', this.uploadAdForm.endDate)
      }
      if (this.uploadAdForm.startTime) {
        form.append('start_time', this.uploadAdForm.startTime)
      }
      if (this.uploadAdForm.endTime) {
        form.append('end_time', this.uploadAdForm.endTime)
      }
      try {
        if (this.isEditMode) {
          await adminPanelApi.updateAd(this.editingAdId, form)
          notifySuccess(this, 'Ad updated successfully.')
          this.uploadModalOpen = false
          this.editingAdId = null
          this.existingAdImage = ''
          this.resetUploadForm()
        } else {
          await adminPanelApi.createAd(form)
          this.uploadModalOpen = false
          this.uploadSuccessModalOpen = false
          this.resetUploadForm()
          this.outcomeVariant = 'upload-success'
          this.outcomeVisible = true
        }
        await this.loadAdsDashboard()
      } catch (e) {
        notifyError(this, e.response?.data?.message || (this.isEditMode ? 'Unable to update ad.' : 'Unable to upload ad.'))
      } finally {
        this.isUploadSubmitting = false
      }
    },
    openUploadSuccessModal() {
      this.uploadModalOpen = false
      this.uploadSuccessModalOpen = true
    },
    dismissUploadSuccess() {
      this.uploadSuccessModalOpen = false
    },
    editUploadedAd() {
      this.uploadSuccessModalOpen = false
      this.uploadModalOpen = true
    },
    async closeUploadSuccessModal() {
      this.uploadSuccessModalOpen = false
      await this.submitUploadAd()
    },
    async openApproveModal(adId) {
      if (adId) this.selectedAdId = adId
      const listAd = this.findAdInLists(adId)
      if (listAd) {
        this.featuredAd = {
          id: listAd.id,
          title: listAd.title,
          description: listAd.description,
          category: listAd.category,
          cta: listAd.cta,
          cta_label: listAd.cta,
          image: listAd.image,
          start_date: listAd.publishAt || listAd.createdAt,
          publish_at: listAd.publishAt,
          company: { name: listAd.companyName, logo: listAd.companyLogo },
        }
        if (listAd.image) {
          this.iphoneThumb = resolveMediaUrl(listAd.image)
        }
      }
      if (this.selectedAdId) {
        try {
          let review = null
          try {
            const res = await adminPanelApi.adReview(this.selectedAdId)
            review = unwrapAdminResponse(res)
          } catch {
            const previewRes = await adminPanelApi.adPreview(this.selectedAdId)
            review = unwrapAdminResponse(previewRes)
          }
          const scheduledAt = review.scheduled_at || {}
          const startDate = review.start_date
            || review.publish_at
            || (scheduledAt.date && scheduledAt.time
              ? `${scheduledAt.date} ${scheduledAt.time}`.trim()
              : scheduledAt.date || '')
          this.featuredAd = {
            id: review.id,
            title: review.title,
            description: review.description,
            category: review.category || review.category_name || review.ad_category || review.type || '',
            cta: review.cta_label || review.cta || review.cta_text,
            cta_label: review.cta_label || review.cta || review.cta_text,
            image: review.image,
            start_date: startDate,
            publish_at: review.publish_at || startDate,
            company: {
              ...(this.featuredAd?.company || {}),
              posted_time: startDate,
            },
          }
          if (review.image) {
            this.iphoneThumb = resolveMediaUrl(review.image)
          }
        } catch {
          // keep list payload when review/preview endpoints fail
        }
      }
      this.approveModalOpen = true
    },
    findAdInLists(adId) {
      const id = Number(adId)
      if (!id) return null
      if (this.featuredAd && Number(this.featuredAd.id) === id) return this.featuredAd
      return this.rows.find(ad => ad && Number(ad.id) === id) || null
    },
    closeApproveModal() {
      this.approveModalOpen = false
    },
    async submitApprove() {
      if (!this.selectedAdId || this.isActionLoading) {
        if (!this.selectedAdId) {
          this.$swal({ title: 'Error', text: 'No ad selected for approval.', icon: 'error' })
        }
        return
      }
      this.isActionLoading = true
      try {
        await adminPanelApi.approveAd(this.selectedAdId)
        this.closeApproveModal()
        this.outcomeVariant = 'ad-approved'
        this.outcomeVisible = true
        await this.loadAdsDashboard()
      } catch (e) {
        this.$swal({
          title: 'Error',
          text: extractApiErrorMessage(e, 'Unable to approve ad.'),
          icon: 'error',
        })
      } finally {
        this.isActionLoading = false
      }
    },
    openRejectForAd(adId) {
      if (adId) this.selectedAdId = adId
      this.rejectModalOpen = true
    },
    openRejectModal() {
      this.rejectModalOpen = true
    },
    closeRejectModal() {
      this.rejectModalOpen = false
      this.rejectReason = ''
    },
    async submitRejectReason() {
      if (!this.selectedAdId) {
        this.$swal({ title: 'Error', text: 'No ad selected for rejection.', icon: 'error' })
        return
      }
      if (!this.rejectReason.trim()) {
        this.$swal({ title: 'Error', text: 'Please provide a rejection reason.', icon: 'error' })
        return
      }
      if (this.rejectReason.trim().length < 10) {
        this.$swal({
          title: 'Error',
          text: 'Rejection reason must be at least 10 characters.',
          icon: 'error',
        })
        return
      }
      this.isActionLoading = true
      try {
        await adminPanelApi.rejectAd({ id: this.selectedAdId, reason: this.rejectReason.trim() })
        this.closeRejectModal()
        this.outcomeVariant = 'ad-removed'
        this.outcomeVisible = true
        await this.loadAdsDashboard()
      } catch (e) {
        this.$swal({
          title: 'Error',
          text: extractApiErrorMessage(e, 'Unable to reject ad.'),
          icon: 'error',
        })
      } finally {
        this.isActionLoading = false
      }
    },
    onOutcomePrimary() {
      if (this.outcomeVariant === 'upload-success') {
        this.$router.push({ name: 'ads-uploaded' })
      }
    },
    goToUploadedAds() {
      this.$router.push({ name: 'ads-uploaded' })
    },
    goToPendingAds() {
      this.$router.push({ name: 'ads-pending' })
    },
    openAdDetail(adId) {
      if (!adId) return
      this.$router.push({ name: 'ad-detail', params: { id: String(adId) } })
    },
    confirmDeleteAd(adId) {
      const id = adId || this.selectedAdId
      if (!id) {
        notifyError(this, 'No ad selected for deletion.')
        return
      }
      confirmAction(
        this,
        { title: 'Delete ad?', text: 'This ad will be removed permanently.', confirmButtonText: 'Delete' },
        async () => {
          try {
            await adminPanelApi.deleteAd(id)
            notifySuccess(this, 'Ad deleted successfully.')
            await this.loadAdsDashboard()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete ad.')
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.ads-management-page {
  padding-top: 0.25rem;
}

.ads-mgmt-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.ads-mgmt-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #18181b;
}

.ads-mgmt-subtitle {
  margin: 0.25rem 0 0;
  color: #6e6b7b;
  font-size: 0.92rem;
}

.ads-mgmt-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.add-ad-btn {
  font-weight: 600;
}

.ads-mgmt-card {
  border: 1px solid #ecebf1;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.ads-mgmt-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid #f0eff5;
  background: #fbfbfd;
}

.ads-mgmt-toolbar__left,
.ads-mgmt-toolbar__right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
}

.entries-select {
  width: 88px;
}

.filter-select {
  min-width: 140px;
  width: 150px;
}

.filter-select--wide {
  min-width: 170px;
  width: 180px;
}

.search-input {
  width: 200px;
  max-width: 100%;
}

.view-toggle {
  display: inline-flex;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.view-toggle__btn {
  border: 0;
  background: transparent;
  padding: 0.4rem 0.55rem;
  color: #6e6b7b;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.is-active {
    background: #7367f0;
    color: #fff;
  }
}

.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #6e6b7b;
}

.empty-state__icon {
  color: #b9b9c3;
  margin-bottom: 0.75rem;
}

.ads-datatable {
  ::v-deep th {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8d8fa1;
    border-top: 0;
    background: #fff;
  }

  ::v-deep td {
    vertical-align: middle;
  }
}

.ad-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.65rem;
}

.ad-thumb {
  width: 48px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: #f3f2f7;
}

.ad-cell__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ad-name {
  font-weight: 600;
  color: #2f2b3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.ad-cat {
  color: #8d8fa1;
}

.company-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.stats-cell {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
  color: #6e6b7b;
  line-height: 1.35;
}

.action-icons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
}

.action-icon {
  color: #7e8294;
  cursor: pointer;
  padding: 0.2rem;

  &:hover {
    color: #5e5873;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: capitalize;
  background: #f0f0f5;
  color: #6e6b7b;

  &.is-success {
    background: #e8f8ef;
    color: #28c76f;
  }

  &.is-warning {
    background: #fff4e5;
    color: #ff9f43;
  }

  &.is-danger {
    background: #fdecea;
    color: #ea5455;
  }

  &.is-muted {
    background: #f0f0f5;
    color: #6e6b7b;
  }

  &--overlay {
    position: absolute;
    top: 0.65rem;
    left: 0.65rem;
  }
}

.ads-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.ads-grid-card {
  border: 1px solid #ecebf1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
}

.ads-grid-card__media {
  position: relative;
  background: #111;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.ads-grid-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ads-grid-card__body {
  padding: 0.85rem 0.95rem 1rem;
}

.ads-grid-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #2f2b3d;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 991.98px) {
  .search-input {
    width: 100%;
  }

  .filter-select,
  .filter-select--wide {
    width: 100%;
    min-width: 0;
  }
}

.featured-approval-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
}

.featured-approval-card--empty {
  height: 100%;
}

.featured-approval-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  height: 100%;
  background: #6d6d6d;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  padding: 1.5rem;
}

.featured-media {
  position: relative;
  height: 100%;
  min-height: 300px;
  border-radius: 14px;
  overflow: hidden;
  background: #121212;
}

.featured-bg-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-height: 300px;
  object-fit: cover;
  z-index: 0;
}

.featured-overlay-left {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 38%;
  max-width: 300px;
  min-width: 200px;
  z-index: 2;
  padding: 1.35rem 1.15rem 4.5rem 1.25rem;
  background: linear-gradient(
    90deg,
    rgba(18, 18, 18, 0.92) 0%,
    rgba(18, 18, 18, 0.88) 65%,
    rgba(18, 18, 18, 0.35) 100%
  );
  color: #fff;
}

.featured-apple-logo {
  background: rgba(255, 255, 255, 0.12) !important;
  padding: 5px;
}

.featured-brand {
  font-weight: 700;
  font-size: 1.02rem;
  color: #fff;
}

.featured-time {
  color: #a0aec0;
  font-size: 0.78rem;
}

.featured-title {
  font-size: 1.28rem;
  font-weight: 800;
  color: #fff;
  margin: 0.85rem 0 0.65rem;
  line-height: 1.2;
}

.featured-desc {
  font-size: 0.82rem;
  color: #e2e8f0;
  line-height: 1.5;
  margin: 0;
}

.featured-hero-caption {
  position: absolute;
  right: 6%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  font-size: clamp(1.5rem, 3.5vw, 2.35rem);
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1.05;
  color: #f59e0b;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.35),
    0 2px 8px rgba(0, 0, 0, 0.45),
    0 0 1px #b45309;
  pointer-events: none;
}

.featured-actions {
  position: absolute;
  right: 1.1rem;
  bottom: 1.1rem;
  left: auto;
  transform: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.6rem;
  z-index: 3;
}

.pill-btn {
  border-radius: 999px !important;
  padding: 0.5rem 1.15rem !important;
  font-weight: 600;
  font-size: 0.88rem;
  color: #111827 !important;
  background: #fff !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  border: none !important;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.action-icon-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-icon-circle--ok {
  background: #28a745;
}

.action-icon-circle--no {
  background: #dc3545;
}

.action-icon-fg {
  color: #fff !important;
}

/* Grid layout: side-by-side when the *content* area is wide enough (container
   query). Bootstrap `md` breakpoints use viewport width, so a wide window with
   a narrow main column still stacked with b-col. */
.approval-top-grid {
  display: grid;
  gap: 1.15rem;
  align-items: stretch;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
}

.approval-top-grid__feature,
.approval-top-grid__upload {
  min-width: 0;
  height: 300px;
}

@media (max-width: 767.98px) {
  .approval-top-grid {
    grid-template-columns: 1fr;
  }

  .approval-top-grid__feature,
  .approval-top-grid__upload {
    height: auto;
  }
}

.upload-drop-zone {
  height: 100%;
  min-height: 300px;
  border-radius: 14px;
  background: #6d6d6d;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  cursor: pointer;
  border: 2px dashed rgba(255, 255, 255, 0.55);
  transition: background 0.15s ease, border-color 0.15s ease;
  box-sizing: border-box;
}

.upload-drop-zone:hover,
.upload-drop-zone.is-drag-active {
  background: #757575;
  border-color: rgba(255, 255, 255, 0.75);
}

.upload-drop-zone.is-drag-active {
  border-color: #fff;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.35);
}

.upload-circle-outline {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.upload-plus-icon {
  color: #fff !important;
}

.upload-text {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: center;
  margin: 0;
  max-width: 220px;
  line-height: 1.45;
}

.ads-section {
  margin-top: 2.25rem;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
}

.view-all-link {
  font-weight: 600;
  color: #111827 !important;
  font-size: 0.9rem;
}

.pending-ad-card {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.07);
}

.pending-img-wrap {
  position: relative;
  background: #111;
}

.pending-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.chip-category {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #000 !important;
  color: #fff !important;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.3rem 0.55rem;
  border-radius: 6px;
}

.pending-avatar-float {
  position: absolute;
  bottom: 10px;
  right: 10px;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.pending-body {
  background: #1a1d24;
  padding: 0.85rem 0.75rem 1rem !important;
}

.pending-card-title {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
}

.pending-card-desc {
  color: #9ca3af;
  font-size: 0.72rem;
  line-height: 1.45;
  margin: 0;
}

.metric-ad-card {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  background: #101823;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
}

.metric-img-wrap {
  position: relative;
}

.metric-img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
}

.chip-active {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.28rem 0.55rem !important;
  background: #22c55e !important;
  border-radius: 6px !important;
}

.metric-body {
  padding: 0.75rem 0.65rem 0.85rem !important;
}

.metric-title {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
}

.metric-time {
  color: #94a3b8;
  font-size: 0.72rem;
  white-space: nowrap;
}

.metric-stats {
  color: #94a3b8;
  font-size: 0.7rem;
  line-height: 1.35;
}

.metric-trash {
  padding: 0.25rem 0.4rem !important;
  border-radius: 6px !important;
  line-height: 1;
}

.mb-50 {
  margin-bottom: 0.35rem;
}

.mt-50 {
  margin-top: 0.35rem;
}

.create-ad-modal {
  position: relative;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.create-ad-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: 0;
  background: transparent;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  z-index: 5;

  img {
    display: block;
    width: 32px;
    height: 32px;
  }
}

.create-ad-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  align-items: start;
}

.create-ad-sidebar {
  background: linear-gradient(180deg, #f4f8ff 0%, #eef4ff 100%);
  border-right: 1px solid #e4e9f5;
  padding: 1.25rem 1.1rem 1rem;
  display: flex;
  flex-direction: column;
}

.create-ad-sidebar__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: #2f2b3d;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.create-ad-title-icon {
  color: #4f7cff;
}

.create-ad-sidebar__subtitle {
  margin: 0.45rem 0 0;
  color: #7a7a8c;
  font-size: 0.88rem;
  line-height: 1.45;
}

.create-ad-illustration {
  position: relative;
  margin: 0.85rem 0 1rem;
  min-height: 80px;
}

.create-ad-illustration__card {
  width: 130px;
  height: 78px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #dbe4ff;
  box-shadow: 0 8px 20px rgba(79, 124, 255, 0.12);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.create-ad-illustration__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 22px;
  border-radius: 6px;
  background: #4f7cff;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
}

.create-ad-illustration__line {
  display: block;
  height: 8px;
  border-radius: 999px;
  background: #e8edfb;
  width: 88%;

  &--short {
    width: 62%;
  }
}

.create-ad-illustration__plus {
  position: absolute;
  right: 1.5rem;
  top: 1.2rem;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  font-weight: 700;
  box-shadow: 0 6px 14px rgba(34, 197, 94, 0.35);
}

.create-ad-steps {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.create-ad-step {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  border: 0;
  background: transparent;
  border-radius: 10px;
  padding: 0.65rem 0.7rem;
  text-align: left;
  color: #6f6b7d;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  width: 100%;

  &.is-active {
    background: rgba(79, 124, 255, 0.12);
    color: #2f2b3d;
  }
}

.create-ad-step__text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.create-ad-step__label {
  font-weight: 700;
  font-size: 0.92rem;
  line-height: 1.25;
  color: inherit;
}

.create-ad-step__hint {
  font-size: 0.78rem;
  font-weight: 500;
  color: #8c8a99;
  line-height: 1.3;
}

.create-ad-step.is-active .create-ad-step__hint {
  color: #6f6b7d;
}

.create-ad-step__num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #d8deea;
  color: #5d596d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 700;
  flex-shrink: 0;
}

.create-ad-step.is-active .create-ad-step__num {
  background: #011425;
  color: #fff;
}

.create-ad-main {
  padding: 1.15rem 1.35rem 1rem;
  display: flex;
  flex-direction: column;

  ::v-deep .mypoda-form-field {
    margin-bottom: 0.55rem;
  }
}

.create-ad-section {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.create-ad-section__head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.create-ad-section__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eef4ff;
  color: #4f7cff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.create-ad-section__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #2f2b3d;
}

.create-ad-section__subtitle {
  margin: 0.2rem 0 0;
  color: #8c8a99;
  font-size: 0.86rem;
}

.create-ad-fields {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.create-ad-input {
  width: 100%;
  border: 1px solid #dcdce6;
  border-radius: 8px;
  color: #58566d;
  font-size: 0.95rem;
  padding: 0.68rem 0.85rem;
  background: #fff;

  &--with-icon {
    padding-left: 2.35rem;
  }
}

.create-ad-input-icon-wrap {
  position: relative;
}

.create-ad-input-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #8c8a99;
  pointer-events: none;
}

.create-ad-field-error {
  color: #ea5455;
  font-size: 0.86rem;
  margin: 0.25rem 0 0;
}

.create-ad-dropzone {
  border: 2px dashed #c8c8d5;
  border-radius: 12px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.15s ease, background 0.15s ease;

  &.is-dragging,
  &:hover {
    border-color: #4f7cff;
    background: #f4f8ff;
  }

  &.has-file {
    background: #fff;
  }
}

.create-ad-dropzone__cloud {
  color: #4f7cff;
  margin-bottom: 0.65rem;
}

.create-ad-dropzone__text {
  margin: 0;
  color: #5d596d;
  font-weight: 600;

  span {
    color: #4f7cff;
  }
}

.create-ad-dropzone__hint {
  margin: 0.45rem 0 0;
  color: #8c8a99;
  font-size: 0.82rem;
  max-width: 420px;
}

.create-ad-dropzone__preview {
  max-height: 90px;
  border-radius: 8px;
  object-fit: contain;
  margin-bottom: 0.65rem;
}

.create-ad-dropzone__filename {
  margin: 0 0 0.5rem;
  color: #5d596d;
  font-size: 0.88rem;
  font-weight: 600;
}

.create-ad-dropzone__change {
  border: 0;
  background: transparent;
  color: #4f7cff;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
}

.create-ad-schedule-summary {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: #eef4ff;
  border-radius: 8px;
  padding: 0.72rem 0.9rem;
  color: #4f7cff;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.create-ad-step-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid #ebe9f1;
}

.create-ad-step-footer__spacer {
  width: 44px;
}

.create-ad-step-footer__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-left: auto;
}

.create-ad-back-btn {
  width: 44px;
  height: 44px;
  border-radius: 8px !important;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.create-ad-next-btn {
  border: none !important;
  border-radius: 8px !important;
  background: #011425 !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 120px;
  justify-content: center;
  padding: 0.72rem 1.35rem !important;
}

.create-ad-cancel-btn {
  border-radius: 8px !important;
  font-weight: 600;
  min-width: 110px;
  padding: 0.68rem 1.25rem !important;
}

.create-ad-submit-btn {
  border: none !important;
  border-radius: 8px !important;
  background: #011425 !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 200px;
  justify-content: center;
  padding: 0.72rem 1.35rem !important;
}

@media (max-width: 991.98px) {
  .create-ad-layout {
    grid-template-columns: 1fr;
  }

  .create-ad-sidebar {
    border-right: 0;
    border-bottom: 1px solid #e4e9f5;
  }

  .create-ad-steps {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .create-ad-step {
    flex: 1 1 calc(50% - 0.35rem);
  }

  .create-ad-main {
    min-height: auto;
  }

  .create-ad-step-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .create-ad-step-footer__spacer {
    display: none;
  }

  .create-ad-step-footer__actions {
    flex-direction: column-reverse;
    width: 100%;
    margin-left: 0;
  }

  .create-ad-next-btn,
  .create-ad-submit-btn,
  .create-ad-cancel-btn,
  .create-ad-back-btn {
    width: 100%;
    min-width: 0;
  }
}

::v-deep .upload-success-modal-wrap .modal-dialog {
  max-width: 900px;
}

::v-deep .upload-success-modal-content {
  border-radius: 10px;
  border: 1px solid #e6e5f0;
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.18);
}

.upload-success-modal-body {
  padding: 1.15rem 2.1rem 1.45rem;
}

.upload-success-modal-title {
  margin: 0 0 0.8rem;
  text-align: center;
  font-size: clamp(1.8rem, 2.8vw, 2.35rem);
  font-weight: 800;
  color: #59566f;
}

.upload-success-modal-img {
  width: min(410px, 100%);
  height: 310px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  margin: 0 auto 1rem;
}

.upload-success-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
}

.upload-success-field {
  width: 100%;
  border: 1px solid #d8d8e3;
  border-radius: 10px;
  background: #fff;
  color: #58566d;
  font-size: 1.05rem;
  line-height: 1.2;
  padding: 0.85rem 0.95rem;
  box-sizing: border-box;
}

.upload-success-field--textarea {
  min-height: 95px;
  resize: none;
}

.upload-success-datetime {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.upload-success-footer {
  display: flex;
  justify-content: flex-end;
}

.upload-success-post-btn {
  min-width: 74px;
  border-radius: 8px !important;
  border: none !important;
  background: #001a3b !important;
  color: #fff !important;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 0.62rem 1rem !important;
}

::v-deep .approve-ad-modal-wrap .modal-dialog {
  max-width: 900px;
}

::v-deep .approve-ad-modal-content {
  border-radius: 10px;
  border: 1px solid #e6e5f0;
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.18);
}

.approve-ad-modal-body {
  padding: 1.8rem 2.1rem 1.6rem;
}

.approve-ad-modal-title {
  margin: 0 0 1rem;
  text-align: center;
  font-size: clamp(1.75rem, 2.8vw, 2.35rem);
  font-weight: 800;
  color: #59566f;
}

.approve-ad-modal-img {
  width: min(410px, 100%);
  height: 340px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  margin: 0 auto 1rem;
}

.approve-ad-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
}

.approve-ad-field {
  width: 100%;
  border: 1px solid #d8d8e3;
  border-radius: 10px;
  background: #fff;
  color: #58566d;
  font-size: 1.05rem;
  line-height: 1.2;
  padding: 0.85rem 0.95rem;
  box-sizing: border-box;
}

.approve-ad-field--textarea {
  min-height: 95px;
  resize: none;
}

.approve-ad-datetime {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.approve-ad-modal-actions {
  display: flex;
  gap: 0.9rem;
  margin-top: 0.35rem;
}

.approve-ad-modal-btn {
  min-width: 105px;
  border-radius: 8px !important;
  border: none !important;
  background: #001a3b !important;
  color: #fff !important;
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1;
  padding: 0.72rem 1.05rem !important;
}

.pending-ad-card {
  cursor: pointer;
}

::v-deep .reject-ad-modal-wrap .modal-dialog {
  max-width: 680px;
}

::v-deep .reject-ad-modal-content {
  border-radius: 22px;
  border: 1px solid #e6e5f0;
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.22);
  padding: 0.45rem;
}

.reject-ad-modal-body {
  padding: 1.6rem 1.85rem 1.45rem;
}

.reject-ad-modal-title {
  margin: 0 0 1rem;
  text-align: center;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.35;
  color: #2f2b3d;
}

.reject-reason-input {
  margin-bottom: 1.25rem;
}

::v-deep .reject-reason-input.form-control {
  min-height: 170px !important;
  border-radius: 8px;
  border: 1px solid #e1e2ef;
  color: #4b4b60;
  font-size: 1.05rem;
  padding: 0.95rem 1rem;
}

::v-deep .reject-reason-input.form-control::placeholder {
  color: #b9b8ca;
}

.reject-ad-modal-actions {
  display: flex;
  justify-content: center;
  gap: 0.9rem;
}

.reject-ad-modal-btn {
  min-width: 94px;
  border-radius: 10px !important;
  border: none !important;
  background: #001a3b !important;
  color: #fff !important;
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1;
  padding: 0.8rem 1rem !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.22);
}

.reject-ad-modal-btn:hover,
.reject-ad-modal-btn:focus {
  background: #0b2950 !important;
}
</style>

<style lang="scss">
// Bootstrap-Vue modals are portaled to <body>, so centering rules must be unscoped.
.create-ad-modal-wrap {
  .modal-dialog {
    max-width: 920px;
    width: calc(100% - 2rem);
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .modal-content {
    border: 0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 48px rgba(17, 24, 39, 0.18);
  }

  .modal-body {
    padding: 0;
  }
}
</style>
